import React from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../context/PermissionsContext';
import { ShieldAlert, ArrowRight, ShieldCheck } from 'lucide-react';

interface PermissionRouteProps {
  route: string;
  screenName: string;
  children: React.ReactNode;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  route,
  screenName,
  children,
}) => {
  const { canAccessRoute, allowedRoutes, role, isLoading } = usePermissions();

  if (isLoading) {
    return <>{children}</>;
  }

  if (!canAccessRoute(route)) {
    // Pick the first allowed route that isn't the current denied route, fallback to /
    const fallbackRoute = allowedRoutes.find((r) => r !== route) || '/';

    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-slate-900/90 border border-rose-500/30 rounded-2xl p-8 text-center shadow-2xl relative overflow-hidden backdrop-blur-xl">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-amber-500 to-rose-500" />

          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400">
            <ShieldAlert className="w-8 h-8" />
          </div>

          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-950/80 text-rose-400 border border-rose-800/80 uppercase tracking-wider">
            Access Restricted (UAM)
          </span>

          <h2 className="text-xl font-bold text-white mt-3 mb-2">
            {screenName} Access Denied
          </h2>

          <p className="text-sm text-slate-400 mb-6 leading-relaxed">
            Your workspace role (<span className="text-amber-400 font-semibold">{role || 'User'}</span>) currently does not have permission to access the route{' '}
            <code className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
              {route}
            </code>.
          </p>

          <div className="flex flex-col gap-3">
            <Link
              to={fallbackRoute}
              className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold bg-brand-600 hover:bg-brand-500 text-white transition flex items-center justify-center gap-2 shadow-lg shadow-brand-600/20"
            >
              <span>Go to Permitted Screen</span>
              <ArrowRight className="w-4 h-4" />
            </Link>

            {(role === 'OWNER' || role === 'ADMIN') && route !== '/uam' && (
              <Link
                to="/uam"
                className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 transition flex items-center justify-center gap-2 border border-slate-700"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Manage Permissions in UAM</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
